import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { shallowEqual } from 'react-redux';

import { RouteContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import { HomePageContent } from 'src/legacy/components/HomePage';
import { SettingsPageContent } from 'src/legacy/components/Settings/SettingsPageContent';
import { useGetPortalConfigQuery } from 'src/services/api';
import { RootState } from 'src/store';
import { ensureApiError } from 'src/utils/Errors';

import { NotAllowedPage } from '../NotAllowedPage';

import { LoadingPage } from './LoadingPage';
import { MfaEnforcementPage } from './MfaEnforcementPage';
import { NetworkErrorPage } from './NetworkErrorPage';

export enum ShowSidebarConfig {
  Show = 'show',
  Hide = 'hide',
  IfUnauthed = 'if-unauthed',
}

export interface PageContentStyleBase {
  showSpace?: boolean;
  fullMobileWidth?: boolean;
  maxWidth?: string;
  drawerWidth: number;
}

export interface PageContentStyleProps extends PageContentStyleBase {
  showToolbar: boolean;
  showSidebar: boolean;
}

export interface PageContentProps extends PageContentStyleBase {
  children?: React.ReactNode;
  allowed?: boolean;
  fullPage?: boolean;
  isSettingsPage?: boolean;
  unauthAccessAllowed?: boolean;
  showToolbar?: boolean;
  showSidebar?: ShowSidebarConfig;
  isAccessLoading?: boolean;
}
const getLeftPadding = (
  props: PageContentStyleProps,
  theme: Theme,
  isSmScreen: boolean,
) => {
  const additionalPadding = !isSmScreen ? props.drawerWidth : 0;
  const paddingValue = `${
    additionalPadding + (props.showSpace ? theme.spacing(4.5) : 0)
  }px`;
  return paddingValue;
};

const usePageContentStyles = makeStyles<Theme, PageContentStyleProps>((theme) =>
  createStyles({
    root: {
      height: '100%',
      overflow: 'auto',
      boxSizing: 'border-box',
      [theme.breakpoints.down('xs')]: {
        padding: (props: PageContentStyleProps) =>
          props.showSpace ? theme.spacing(0, 2) : 0,
      },
      [theme.breakpoints.up('sm')]: {
        height: '100%',
        padding: (props: PageContentStyleProps) =>
          props.showSpace ? theme.spacing(0, 4.5) : 0,
        paddingLeft: (props: PageContentStyleProps) =>
          props.showSidebar ? getLeftPadding(props, theme, true) : 0,
      },
      [theme.breakpoints.up('md')]: {
        paddingLeft: (props: PageContentStyleProps) =>
          props.showSidebar ? getLeftPadding(props, theme, false) : 0,
      },
    },
    noSpaceContainer: {
      [theme.breakpoints.down('xs')]: {
        margin: '0 auto',
      },
    },
    noToolbar: (props: PageContentStyleProps) => ({
      paddingTop: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        paddingTop: props.showSpace ? theme.spacing(2) : 0,
      },
    }),
    pageContentBody: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: '100%',
        padding: 'initial',
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: '100%',
      },
      width: '100%',
      height: '100%',
      display: 'flex',
      margin: 'initial',
      flexDirection: 'column',
    },
    fullPage: {
      padding: 0,
      width: '100%',
      height: '100%',
    },
  }),
);

export const PageContent = forwardRef<HTMLDivElement, PageContentProps>(
  (
    {
      children,
      allowed = true,
      showToolbar = true,
      fullPage = false,
      isSettingsPage = false,
      unauthAccessAllowed = false,
      showSpace = true,
      showSidebar = ShowSidebarConfig.Show,
      isAccessLoading = false,
      ...styleProps
    },
    ref,
  ) => {
    const { isLoading: isPortalConfigurationLoading, error } =
      useGetPortalConfigQuery(); // Check whether portal configuration is loaded
    const portalConfigError = ensureApiError(error);

    const { loadingError, isLoggedInUser, userLoaded } = useAppSelector(
      (state: RootState) => ({
        loadingError: state.user.loadingError,
        isLoggedInUser: Boolean(state.user.instance),
        userLoaded: state.user.loaded,
      }),
      shallowEqual,
    );

    const { pathname } = React.useContext(RouteContext);
    const isClientUser = useAppSelector((state) => state.user.isClient);
    const classes = usePageContentStyles({
      ...styleProps,
      showToolbar,
      showSpace,
      showSidebar:
        showSidebar === ShowSidebarConfig.IfUnauthed
          ? isLoggedInUser
          : showSidebar === ShowSidebarConfig.Show,
    });

    const redirectToHomePage =
      (pathname === '/' || pathname.startsWith('/portal/')) && !isClientUser;

    let PageOverrideComponent;
    if (portalConfigError.code === 'mfa_required') {
      PageOverrideComponent = MfaEnforcementPage;
    } else if (loadingError) {
      PageOverrideComponent = NetworkErrorPage;
    } else if (!allowed && !isAccessLoading && userLoaded) {
      PageOverrideComponent = NotAllowedPage;
    } else if (
      isPortalConfigurationLoading ||
      isAccessLoading ||
      (!userLoaded && !unauthAccessAllowed)
    ) {
      PageOverrideComponent = LoadingPage;
    }

    const noSpaceContainerClassName = classNames(
      classes.root,
      classes.noSpaceContainer,
      {
        [classes.fullPage]: !showToolbar,
      },
    );

    if (PageOverrideComponent) {
      return (
        <div className={`${noSpaceContainerClassName} flex w-full`} ref={ref}>
          <PageOverrideComponent />
        </div>
      );
    }

    if (isSettingsPage) {
      return (
        <SettingsPageContent drawerWidth={styleProps.drawerWidth}>
          {children}
        </SettingsPageContent>
      );
    }

    if (fullPage) {
      return (
        <div className={classes.fullPage} ref={ref}>
          {children}
        </div>
      );
    }

    if (!showSpace) {
      return (
        <div className={`${noSpaceContainerClassName} w-full`} ref={ref}>
          {children}
        </div>
      );
    }
    return (
      <Grid
        container
        className={classNames(classes.root, {
          [classes.noToolbar]: !showToolbar,
        })}
        ref={ref}
      >
        <Grid className={classes.pageContentBody} item>
          {redirectToHomePage ? <HomePageContent /> : children}
        </Grid>
      </Grid>
    );
  },
);
